import {inject, Injectable} from '@angular/core';
import {RegistrationRequest} from '../dto/request/RegistrationRequest';
import {BehaviorSubject, catchError, map, Observable, of, switchMap} from 'rxjs';
import {ResponseDto} from '../../common/dto/response/ResponseDto';
import {RegistrationResponse} from '../dto/response/RegistrationResponse';
import {ApiClient} from '../../common/infrastructure/network/ApiClient';
import {ErrorResponseDto} from '../../common/dto/response/ErrorResponseDto';
import {ResultDto} from '../../common/dto/ResultDto';
import {TokenService} from './token-service';
import {LoginRequest} from '../dto/request/LoginRequest';
import {TokenResponse} from '../dto/response/TokenResponse';
import {RefreshRequest} from '../dto/request/RefreshRequest';
import {BlockTokensRequest} from '../dto/request/BlockTokensRequest';
import {BlockTokensResponse} from '../dto/response/BlockTokensResponse';
import {ErrorHandler} from '../../common/service/ErrorHandler';
import {ChangePasswordRequest} from '../dto/request/ChangePasswordRequest';
import {ChangePasswordResponse} from '../dto/response/ChangePasswordResponse';
import {ForgotPasswordRequest} from '../dto/request/ForgotPasswordRequest';
import {ResetPasswordRequest} from '../dto/request/ResetPasswordRequest';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ErrorHandler {

  private readonly registerUrl = 'users/auth/signup';
  private readonly loginUrl = 'users/auth/signin';
  private readonly refreshUrl = 'users/auth/refresh-token';
  private readonly blockTokensUrl = 'users/auth/block-tokens';
  private readonly changePasswordUrl = 'users/auth/change-password';
  private readonly forgotPasswordUrl = 'users/auth/reset-password-request';
  private readonly resetPasswordUrl = 'users/auth/reset-password';

  private apiClient: ApiClient = inject(ApiClient);
  private tokenService: TokenService = inject(TokenService);

  private authSubject = new BehaviorSubject<boolean>(false);
  private isCheckingAuth = false;

  public register(email: string, password: string, confirmation: string): Observable<ResultDto<boolean | undefined>> {
    const request = new RegistrationRequest(email, password, confirmation);
    return this.apiClient.post<RegistrationRequest, ResponseDto<RegistrationResponse | ErrorResponseDto>>(this.registerUrl, request)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp: RegistrationResponse = response.data as RegistrationResponse;
            this.tokenService.saveTokens(resp.tokens.access, resp.tokens.refresh);
            this.authSubject.next(true);
            return ResultDto.success(true);
          }
          return ResultDto.error('Registration failed');
        }),
        catchError(err => this.handleError(err))
      );
  }

  public login(email: string, password: string): Observable<ResultDto<boolean | undefined>> {
    const request = new LoginRequest(email, password);
    return this.apiClient.post<LoginRequest, ResponseDto<TokenResponse | ErrorResponseDto>>(this.loginUrl, request)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp: TokenResponse = response.data as TokenResponse;
            this.tokenService.saveTokens(resp.access, resp.refresh);
            this.authSubject.next(true);
            return ResultDto.success(true);
          }
          this.authSubject.next(false);
          return ResultDto.error('Login failed');
        }),
        catchError(err => this.handleError(err))
      )
  }

  public refreshTokens(refreshToken: string): Observable<ResultDto<boolean | undefined>> {
    const request = new RefreshRequest(refreshToken);
    return this.apiClient.post<RefreshRequest, ResponseDto<TokenResponse | ErrorResponseDto>>(this.refreshUrl, request)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp: TokenResponse = response.data as TokenResponse;
            this.tokenService.saveTokens(resp.access, resp.refresh);
            return ResultDto.success(true);
          }
          return ResultDto.error('Refresh failed');
        }),
        catchError(err => this.handleError(err))
      )
  }

  public isAuthenticated(): Observable<boolean> {
    if (this.isCheckingAuth) {
      return this.authSubject.asObservable();
    }
    const accessToken = this.tokenService.getAccessToken();
    if (accessToken) {
      if (this.tokenService.isTokenValid(accessToken)) {
        if (!this.authSubject.value) {
          this.authSubject.next(true);
        }
        return this.authSubject.asObservable();
      }
      const refreshToken = this.tokenService.getRefreshToken();
      if (refreshToken) {
        if (this.tokenService.isTokenValid(refreshToken)) {
          this.isCheckingAuth = true;
          return this.refreshTokens(refreshToken)
            .pipe(
              switchMap(result => {
                const isValid = !!result.data;
                if (this.authSubject.value !== isValid) {
                  this.authSubject.next(isValid);
                }
                this.isCheckingAuth = false;
                return this.authSubject.asObservable();
              }),
              catchError(err => {
                this.handleError(err);
                this.isCheckingAuth = false;
                this.authSubject.next(false);
                return this.authSubject.asObservable();
              })
            );
        }
      }
      this.authSubject.next(false);
      this.tokenService.clearTokens();
    } else if (this.authSubject.value) {
      this.authSubject.next(false);
    }
    return this.authSubject.asObservable();
  }

  public changePassword(oldPassword: string, newPassword: string): Observable<ResultDto<boolean | undefined>> {
    const request = new ChangePasswordRequest(oldPassword, newPassword, newPassword);
    return this.apiClient.post<ChangePasswordRequest, ResponseDto<ChangePasswordResponse | ErrorResponseDto>>(this.changePasswordUrl, request)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp = response.data as ChangePasswordResponse;
            if (resp.success) {
              return ResultDto.success(true);
            }
            return ResultDto.error('Password change failed');
          }
          return ResultDto.error('Password change failed');
        }),
        catchError(err => this.handleError(err))
      );
  }

  public requestPasswordReset(email: string): Observable<ResultDto<boolean | undefined>> {
    const request = new ForgotPasswordRequest(email);
    // Backend replies 204 No Content on success (empty body), so any emission means success.
    return this.apiClient.post<ForgotPasswordRequest, void>(this.forgotPasswordUrl, request)
      .pipe(
        map(() => ResultDto.success(true)),
        catchError(err => this.handleError(err))
      );
  }

  public resetPassword(email: string, token: string, password: string, confirmation: string): Observable<ResultDto<boolean | undefined>> {
    const request = new ResetPasswordRequest(email, token, password, confirmation);
    // Backend replies 204 No Content on success (empty body), so any emission means success.
    return this.apiClient.post<ResetPasswordRequest, void>(this.resetPasswordUrl, request)
      .pipe(
        map(() => ResultDto.success(true)),
        catchError(err => this.handleError(err))
      );
  }

  public logout(): Observable<ResultDto<boolean | undefined>> {
    const accessToken = this.tokenService.getAccessToken();
    const refreshToken = this.tokenService.getRefreshToken();
    if (accessToken && refreshToken) {
      const request = new BlockTokensRequest(accessToken, refreshToken);
      return this.apiClient.post<BlockTokensRequest, ResponseDto<BlockTokensResponse | ErrorResponseDto>>(this.blockTokensUrl, request)
        .pipe(
          map((response) => {
            if (response.success && response.data) {
              const resp: BlockTokensResponse = response.data as BlockTokensResponse;
              if (resp.success) {
                this.tokenService.clearTokens();
                this.authSubject.next(false);
                return ResultDto.success(resp.success);
              }
              return ResultDto.error('Logout failed');
            }
            return ResultDto.error('Logout failed');
          }),
          catchError(err => this.handleError(err))
        )
    }
    console.warn('Logout failed: one or both tokens are missing');
    this.tokenService.clearTokens();
    this.authSubject.next(false);
    return of(ResultDto.success(true));
  }

  /**
   * Clears the session without calling the block-tokens API.
   * Used when the tokens are already invalid server-side, e.g. after the account was deleted.
   */
  public logoutLocally(): void {
    this.tokenService.clearTokens();
    this.authSubject.next(false);
  }

}
